// @ts-nocheck
/*eslint-disable import/namespace*/
import * as Yup from 'yup';
import isArray from 'lodash/isArray';
import cloneDeep from 'lodash/cloneDeep';

import { parseSafeString } from '../parsing';
import {
  EMPTY_OBJECT,
  PARAGRAPH_SCHEMA,
  ARRAY_SCHEMA,
} from '../../const/CommonConst';
import { normalizeParagraphValue } from '../../utils/core';

/**
 * Type guard to check if a given value is a valid health status object.
 * A valid health status object must contain 'label', 'color', and 'fail_reason' properties.
 *
 * @param {any} value - The value to check.
 * @returns {boolean} True if the value is a valid health status object, false otherwise.
 */
export const isHealthStatusObject = (value) =>
  value &&
  typeof value === 'object' &&
  'label' in value &&
  'color' in value &&
  'fail_reason' in value;

export const createSafeStringValidator = (validator) => {
  return validator.transform((value, originalValue) => {
    // If it's a valid health object, extract its label so it passes string validation.
    if (isHealthStatusObject(originalValue)) {
      return originalValue?.label || '';
    }

    return value;
  });
};

const getDataType = (inputControlType, dataType) => {
  const dataTypeMap = {
    Character: 'string',
    Integer: 'number',
    Decimal: 'number',
    Date: 'date',
    Paragraph: 'object',
    Checklist: 'object',
    Association: 'object',
    string: 'string',
    number: 'number',
    date: 'date',
    object: 'object',
    Hyperlink: 'string',
    Document: 'array',
    User: 'string',
    AttributeList: 'object',
    ParameterTableList: 'object',
    EmailTemplateList: 'object',
    TemplateList: 'object',
    FieldChangeActions: 'object',
    InventoryView: 'array',
  };

  let jsDataType = dataTypeMap?.[dataType];

  if (inputControlType === 'MULTI_SELECT') {
    jsDataType = 'array';
  }

  return jsDataType;
};

export const createYupSchema = (schema = EMPTY_OBJECT, config) => {
  const {
    id,
    name,
    label,
    data_type,
    yup_schema,
    input_control_type,
    is_null,
    is_derived,
    isAttributeDisabled,
  } = config;
  const _schema = cloneDeep(schema);

  if (!isArray(yup_schema) && input_control_type !== 'MULTI_SELECT') {
    return schema;
  }

  // If the attribute is derived then just skip the creation of Yup schema for that attribute
  if (is_derived) {
    return schema;
  }

  // If the isAttributeDisabled then just skip the creation of Yup schema for that attribute
  if (isAttributeDisabled) {
    return schema;
  }

  const dataType = getDataType(input_control_type, data_type);
  const displayLabel = label || name || 'This field';
  const hasRequiredRule =
    Array.isArray(yup_schema) &&
    yup_schema.some((rule) => rule?.type === 'required');

  // Handle nested object types (Paragraph, Checklist etc.)
  if (dataType === 'object' && Array.isArray(yup_schema)) {
    const nestedShape = yup_schema.reduce((acc, fieldConfig) => {
      // recurse: each child must keep its own name
      const childSchema = createYupSchema({}, fieldConfig);
      return { ...acc, ...childSchema };
    }, {});

    let validator = Yup.object().shape(nestedShape);

    if (data_type === 'Paragraph') {
      validator = validator.transform((value, originalValue) => {
        return normalizeParagraphValue(originalValue);
      });
    } else {
      validator = validator.typeError(
        `${displayLabel} must be a valid ${data_type || ''}`
      );
    }

    if (is_null || !hasRequiredRule) {
      validator = validator.nullable();
    }
    if (is_null) {
      validator = validator.default(null);
    }

    // if parent object itself has a required rule
    const objectRequired = yup_schema.find((v) => v.type === 'required');
    if (objectRequired) {
      const objectParams =
        objectRequired.params?.[0] === 'Field is required'
          ? ['This field is required', ...objectRequired.params.slice(1)]
          : objectRequired.params;
      validator = validator.required(...(objectParams || []));
    }

    _schema[id ?? name] = validator;
    return _schema;
  }

  if (!Yup[dataType]) {
    return schema;
  }

  let validator = Yup[dataType]();
  validator = validator.label(displayLabel);

  // If it's a string schema, we must gracefully handle Health objects
  // since the backend might send a Health object for a Character field.
  // We transform it to its label string ONLY for validation purposes.
  if (dataType === 'string') {
    validator = createSafeStringValidator(validator);
  }

  if (is_null || !hasRequiredRule) {
    validator = validator.nullable();
  }
  if (is_null) {
    validator = validator.default(null);
  }

  if (dataType === 'number') {
    const isInteger = data_type === 'Integer';
    const numberTypeMessage = isInteger
      ? `${displayLabel} must be a valid integer`
      : `${displayLabel} must be a valid number`;

    validator = validator
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === null ||
          originalValue === undefined ||
          (typeof originalValue === 'string' && originalValue.trim() === '')
        ) {
          return null;
        }
        return value;
      })
      .typeError(numberTypeMessage)
      .min(
        Number.MIN_SAFE_INTEGER,
        `The value falls short of the minimum allowable range ${Number.MIN_SAFE_INTEGER}`
      )
      .max(
        Number.MAX_SAFE_INTEGER,
        `The value exceeds the maximum allowable range of ${Number.MAX_SAFE_INTEGER}`
      );
  }
  if (dataType === 'date') {
    validator = validator
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === null ||
          originalValue === undefined ||
          (typeof originalValue === 'string' && originalValue.trim() === '')
        ) {
          return null;
        }
        return value;
      })
      .typeError(`${displayLabel} must be a valid date`);
  }
  if (dataType === 'array') {
    validator = validator
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === null ||
          originalValue === undefined ||
          (typeof originalValue === 'string' && originalValue.trim() === '')
        ) {
          return null;
        }
        return value;
      })
      .typeError(`${displayLabel} must be valid`);
  }

  // apply validations
  if (Array.isArray(yup_schema)) {
    yup_schema.forEach((rule) => {
      const { type, params, vendor } = rule;
      if (type === 'when' && vendor === 'HDFC') {
        const [dependentKey, condition] = params;
        validator = validator.when(dependentKey, (value, whenSchema) => {
          const now = new Date();

          const valuesMap = Array.isArray(dependentKey)
            ? dependentKey.reduce((acc, key, index) => {
                // eslint-disable-next-line no-param-reassign
                acc[String(key)] = value?.[index];
                return acc;
              }, {})
            : { [String(dependentKey)]: value };

          const applyRule = (whenRule) => {
            // return if type does not exist
            if (!whenRule?.type) {
              return whenSchema;
            }
            const [months, message] = whenRule.params;
            const maxDate = new Date(now);
            maxDate.setMonth(now.getMonth() + months);
            return whenSchema[whenRule.type](maxDate, message);
          };

          const evaluateCondition = (cond) => {
            if (cond.operator && cond.conditions) {
              if (cond.operator === 'AND') {
                return cond.conditions.every(evaluateCondition);
              } else if (cond.operator === 'OR') {
                return cond.conditions.some(evaluateCondition);
              }
            }

            const fieldValue = valuesMap[String(cond.key)];
            return cond.is === fieldValue;
          };

          let matched;
          if (condition.operator && condition.conditions) {
            if (condition.operator === 'AND') {
              matched = condition.conditions.every(evaluateCondition);
            } else if (condition.operator === 'OR') {
              matched = condition.conditions.some(evaluateCondition);
            }
          } else {
            matched = condition.conditions.every((cond) => {
              const fieldValue = valuesMap[String(cond.key)];
              return cond.is === fieldValue;
            });
          }

          if (matched) {
            return applyRule(condition.then);
          } else {
            return condition.otherwise
              ? applyRule(condition.otherwise)
              : whenSchema;
          }
        });
      } else if (validator[type]) {
        const normalizedParams =
          type === 'required' && params?.[0] === 'Field is required'
            ? ['This field is required', ...params.slice(1)]
            : params;
        validator = validator[type](...(normalizedParams || []));
      }
    });
  }

  _schema[id ?? name] = validator;
  return _schema;
};

export const updateYupSchemaValidation = (
  yupSchema,
  isRemoveRequired = false,
  dataType = 'Character',
  inputControlType = 'TEXTFIELD'
) => {
  const parsedSchema = parseSafeString(yupSchema, { schema: [{}] });
  const schema = parsedSchema?.schema || [{}];
  const isSpecialSchemaType =
    dataType === 'Paragraph' ||
    dataType === 'Document' ||
    dataType === 'InventoryView' ||
    inputControlType === 'MULTI_SELECT';

  const getRequiredSchema = () => {
    if (dataType === 'Paragraph') {
      return PARAGRAPH_SCHEMA;
    }

    if (
      dataType === 'Document' ||
      dataType === 'InventoryView' ||
      inputControlType === 'MULTI_SELECT'
    ) {
      return ARRAY_SCHEMA;
    }

    return null; // Return null for empty strings
  };

  if (isRemoveRequired) {
    const updatedSchema = schema?.filter(
      (validation) => validation?.type !== 'required'
    );

    if (isSpecialSchemaType) {
      return { schema: [{}] };
    }

    if (updatedSchema?.length === 0) {
      return { schema: [{}] };
    }

    return {
      schema: updatedSchema,
    };
  }

  // to remove empty object form array
  const filteredSchema = schema?.filter(
    (validation) => Object.keys(validation || {})?.length > 0
  );

  // Check if the 'required' validation type exists in the schema array
  const isRequiredPresent = filteredSchema?.some(
    (validation) => validation?.type === 'required'
  );

  // If 'required' is not present, add it to the schema
  if (!isRequiredPresent) {
    const requiredSchema = getRequiredSchema();

    if (requiredSchema) {
      return requiredSchema;
    }

    filteredSchema?.push({
      type: 'required',
      params: ['This field is required'], // Message for required field
    });
  }

  return {
    schema: filteredSchema,
  };
};

export const getErrorHelperText = (error, touched) => {
  return touched ? error : null;
};

/**
 * @typedef OutlierTest
 * @property {string} type
 * @property {Array} params
 * @property {string} [message]
 * @property {string} [color = "error"]
 *
 * @typedef OutlierSchema
 * @property {string} type
 * @property {OutlierTest[]} tests
 *
 * @param {any} value
 * @param {OutlierSchema} outlierSchema
 */
export const getOutlierFromOutlierSchema = (value, outlierSchema) => {
  let message;
  let color;
  const baseSchema = Yup[outlierSchema?.type]();

  outlierSchema?.tests?.forEach((test) => {
    const params = test?.params || [];
    const testSchema = baseSchema?.[test?.type](...params);
    const isValid = testSchema?.isValidSync(value);

    if (isValid) {
      message = test?.params[1] || 'Warning';
      color = test?.color || 'error';
    }
  });

  return {
    message,
    color,
  };
};

// Check if field is required based on dynamic is_mandatory flag or yup_schema
export const checkAttributeIsRequired = (attribute) => {
  if (
    attribute?.is_mandatory !== undefined &&
    attribute?.is_mandatory !== null
  ) {
    return attribute.is_mandatory === true;
  }

  let schema = attribute?.yup_schema;

  //for paragraph schema
  if (attribute?.data_type === 'Paragraph') {
    schema = schema?.[0]?.yup_schema;
  }

  // Handle case where yup_schema is a string (JSON)
  if (typeof schema === 'string') {
    try {
      const parsed = JSON.parse(schema);
      schema = parsed?.schema || [];
    } catch {
      schema = [];
    }
  }

  // Ensure schema is an array before calling .some()
  if (!Array.isArray(schema)) {
    return false;
  }

  return schema.some((validation) => validation?.type === 'required');
};
